import { ReactNode, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { Popover } from '@equinor/eds-core-react'
import { Sensor } from '@/features/dashboard/models'
import { formatTimestampForDisplay } from '@/utils'

interface SignalDetailsTooltipProps {
    sensor: Sensor
    children: ReactNode
}

const DESKTOP_MIN_WIDTH = 1025
const HOVER_DELAY_MS = 1500

const formatMetric = (value?: number | null, unit = ''): string => (value == null ? '-' : `${value}${unit}`)

// Most recent timestamp across the available radio metrics.
const latestSignalTime = (sensor: Sensor): string | null | undefined => {
    const times = [sensor.sf?.time, sensor.rssi?.time, sensor.snr?.time].filter((time): time is string => Boolean(time))
    if (times.length === 0) return null
    return times.reduce((latest, current) => (new Date(current) > new Date(latest) ? current : latest))
}

// Desktop-only tooltip that reveals the raw radio metrics behind the signal quality
// (SF, RSSI, SNR) and when they were last received. Uses an EDS Popover so it renders
// in a portal and is not clipped by the card's overflow. On touch/mobile widths it is
// disabled (hover does not exist there).
const SignalDetailsTooltip = ({ sensor, children }: SignalDetailsTooltipProps) => {
    const anchorRef = useRef<HTMLDivElement>(null)
    const [open, setOpen] = useState(false)
    const openTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

    const isDesktop = () => window.innerWidth >= DESKTOP_MIN_WIDTH

    const clearOpenTimer = () => {
        if (openTimer.current) {
            clearTimeout(openTimer.current)
            openTimer.current = undefined
        }
    }

    // Reveal only after the pointer/focus lingers, so the tooltip does not flash while
    // scanning across cards.
    const scheduleShow = () => {
        if (!isDesktop()) return
        clearOpenTimer()
        openTimer.current = setTimeout(() => setOpen(true), HOVER_DELAY_MS)
    }

    const hide = () => {
        clearOpenTimer()
        setOpen(false)
    }

    useEffect(() => clearOpenTimer, [])

    return (
        <>
            <Anchor
                ref={anchorRef}
                tabIndex={0}
                aria-label="Signal details"
                onMouseEnter={scheduleShow}
                onMouseLeave={hide}
                onFocus={scheduleShow}
                onBlur={hide}
            >
                {children}
            </Anchor>
            {open && anchorRef.current && (
                <Popover anchorEl={anchorRef.current} open onClose={hide} placement="top">
                    <Popover.Content>
                        <Grid>
                            <Label>SF</Label>
                            <Value>{formatMetric(sensor.sf?.value)}</Value>
                            <Label>RSSI</Label>
                            <Value>{formatMetric(sensor.rssi?.value, ' dBm')}</Value>
                            <Label>SNR</Label>
                            <Value>{formatMetric(sensor.snr?.value, ' dB')}</Value>
                            <Label>Last received</Label>
                            <Value>{formatTimestampForDisplay(latestSignalTime(sensor))}</Value>
                        </Grid>
                    </Popover.Content>
                </Popover>
            )}
        </>
    )
}

const Anchor = styled.div`
    display: inline-flex;
    align-items: center;
    outline: none;
`

const Grid = styled.div`
    display: grid;
    grid-template-columns: auto auto;
    column-gap: 16px;
    row-gap: 4px;
    white-space: nowrap;
`

const Label = styled.span`
    color: #6f6f6f;
`

const Value = styled.span`
    color: #3d3d3d;
    text-align: right;
`

export default SignalDetailsTooltip
